import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiRepeat, FiPrinter, FiLink
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, PaymentSection,
  SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import { createTransaction, updateTransaction, deleteTransaction, getTransactions, updateTransactionStatus } from '../../utils/transactionHelper';
import {  } from '../../utils/subTransactionHelper';
import { getAvailableBatches, getAvailableSerials, invalidateItemStock } from '../../utils/stockFlowHelper';
import { getVendorsCached, getItemsCached, getAccountsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import VendorSelect from '../../components/VendorSelect';
import { isFutureDate } from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import AttachmentPanel from '../../components/AttachmentPanel';
import { recalcInvoiceReturns, getReturnedQtyByItem, capReturnableLines } from '../../utils/invoiceClearingHelper';
import { prewarmSave } from '../../utils/transactionHelper';
import { inActiveBranch } from '../../utils/branchLink';

const TRAN_TYPE_CODE = 'PR';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted', 'Cancelled']
};

const EMPTY_LINE = {
  itemId: '',
  itemCode: '',
  itemName: '',
  description: '',
  unitOfMeasure: '',
  unitCode: '',
  originalQty: 0,
  returnQty: 0,
  unitPrice: 0,
  unitCost: 0,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  lineTotal: 0,
  trackingType: 'None',
  batchNumber: '',
  serialNumbers: []
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
  Cancelled: colors.error.main,
};

const PurchaseReturn = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'purchaseReturn');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);


  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: returns,
    loading,
    refresh: loadReturns,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingReturn, setEditingReturn] = useState(null);
  const [viewingReturn, setViewingReturn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '',
    partyName: '',
    parentId: '',
    parentDocNumber: '',
    reason: '',
    notes: '',
    status: 'Draft',
    whtRate: 0,
    whtAmount: 0,
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
    existingPaidAmount: 0
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [vendors, setVendors] = useState([]);
  const [postedInvoices, setPostedInvoices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});
  const [availableSerials, setAvailableSerials] = useState({});
  // NOTE: no stockBalances here — this form has no item picker (lines come from
  // the parent invoice), so the old mount-time whole-history stock scan fed
  // nothing at all. Removed 2026-08 (Firebase cost).

  // Returns list now comes from useDocListPagination above (loadReturns = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): vendors/items/accounts come from the
  // device-local master cache (getMaster* → IndexedDB, 0 reads per open with a
  // throttled background change-check) instead of a full getDocs on every open.
  // Each screen keeps its own client-side filter/sort on the returned rows.
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) { console.error('Error loading vendors:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the loader below feeds ONLY the form's
  // "Link to Purchase Invoice" dropdown — nothing on the list view uses it. It
  // used to run on every page open AND after every save/delete, scanning full
  // document history (every Posted + Partial + Paid PI ever). It now loads
  // ONCE, lazily, the first time a form is actually opened. List browsing and
  // saving cost nothing.
  const formDataLoadedRef = useRef(false);

  // Load posted invoices from unified transactions collection
  const loadPostedInvoices = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Firebase cost: scope the parent-invoice list to the ACTIVE financial year
      // (returns are normally filed against this year's invoices). A prior-year
      // invoice won't appear in the picker — rare, and a return can still be
      // created from the invoice itself if needed.
      const fyScope = activeFinancialYear?.id ? { financialYearId: activeFinancialYear.id } : {};
      const postedPIs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'PI', status: 'Posted', ...fyScope
      });
      const partialPIs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'PI', status: 'Partial', ...fyScope
      });
      const paidPIs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'PI', status: 'Paid', ...fyScope
      });
      setPostedInvoices([...postedPIs, ...partialPIs, ...paidPIs]);
    } catch (error) { console.error('Error loading invoices:', error); }
  }, [activeCompany, activeFinancialYear]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadPostedInvoices()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadPostedInvoices]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations([...rows].sort((a, b) => (a.locationName || '').localeCompare(b.locationName || '')));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadPaymentAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const allAccts = await getAccountsCached(activeCompany.id);
      const allAccounts = allAccts.filter(a => a.level === 5 || a.isPostable);
      setPaymentAccounts(allAccounts.filter(a => a.code?.startsWith('11101') || a.code?.startsWith('11102')));
    } catch (error) { console.error('Error loading payment accounts:', error); }
  }, [activeCompany]);

  // NOTE: the returns list itself loads inside useDocListPagination — not here.
  // D3: the posted-invoice loader is NOT here anymore — it loads lazily on first
  // form open (ensureFormData), since only the form uses it.
  useEffect(() => {
    loadVendors();
    loadEmployees(); loadJobs(); loadLocations(); loadPaymentAccounts(); loadItems();
  }, [loadVendors, loadEmployees, loadJobs, loadLocations, loadPaymentAccounts, loadItems]);

  // Filter
  const filteredReturns = useMemo(() => {
    let filtered = returns;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) ||
        r.partyName?.toLowerCase().includes(q) ||
        r.parentDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [returns, statusFilter, searchQuery, sort]);

  // Calculate line
  const calculateLine = (line) => {
    const net = (line.returnQty || 0) * (line.unitPrice || 0);
    const tax = line.taxable ? net * ((line.taxRate || 0) / 100) : 0;
    return {
      ...line,
      taxAmount: Math.round(tax * 100) / 100,
      lineTotal: Math.round((net + tax) * 100) / 100
    };
  };

  // Totals
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + ((l.returnQty || 0) * (l.unitPrice || 0)), 0);
    const taxAmount = lines.reduce((s, l) => s + (l.taxAmount || 0), 0);
    const whtAmount = Number(formData.whtRate) > 0 ? subtotal * (Number(formData.whtRate) / 100) : 0;
    // Total after WHT deduction
    const totalAmount = subtotal + taxAmount - whtAmount;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      whtAmount: Math.round(whtAmount * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100
    };
  }, [lineItems, formData.whtRate]);

  // Form handlers
  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData(prev => ({
      ...prev, partyId: vendorId, partyName: vendor?.vendorName || '',
      parentId: '', parentDocNumber: ''
    }));
    setLineItems([{ ...EMPTY_LINE }]);
  };

  const handleInvoiceChange = (invoiceId) => {
    const inv = postedInvoices.find(i => i.id === invoiceId);
    if (!inv) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '' }));
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    // ACC-11: cap each line at what is still returnable (invoice qty minus
    // quantities already returned on other live returns of this invoice).
    getReturnedQtyByItem(activeCompany.id, invoiceId, editingReturn?.id || null)
      .then(returnedByItem => {
        if (Object.keys(returnedByItem).length > 0) setLineItems(prev => capReturnableLines(prev, returnedByItem));
      })
      .catch(err => console.error('Could not load already-returned quantities:', err));
    setFormData(prev => ({
      ...prev, parentId: invoiceId,
      parentDocNumber: inv.docNumber,
      partyId: inv.partyId, partyName: inv.partyName
    }));
    // Auto-fill lines from invoice
    const invLines = inv.lines || [];
    if (invLines.length > 0) {
      const newLines = invLines.map(l => {
        const item = items.find(it => it.id === l.itemId);
        const trackingType = item?.trackingType || 'None';
        return {
          ...EMPTY_LINE,
          itemId: l.itemId || '',
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description || '',
          unitOfMeasure: l.unitOfMeasure || '',
          originalQty: l.quantity || 0,
          returnQty: 0,
          unitPrice: l.unitPrice || 0,
          unitCost: l.unitCost || l.unitPrice || 0,
          taxable: l.taxable || false,
          taxRate: l.taxRate ?? 0,
          trackingType,
          batchNumber: '',
          serialNumbers: []
        };
      });
      setLineItems(newLines);
      // Load available batches/serials for tracked items
      newLines.forEach((l, idx) => {
        if (l.itemId && l.trackingType === 'Batch') {
          getAvailableBatches(activeCompany.id, l.itemId).then(batches => {
            setAvailableBatches(prev => ({ ...prev, [l.itemId]: batches }));
            if (batches.length > 0 && !l.batchNumber) {
              setLineItems(prev => {
                const copy = [...prev];
                if (copy[idx] && !copy[idx].batchNumber) {
                  copy[idx] = { ...copy[idx], batchNumber: batches[0].batchNumber };
                }
                return copy;
              });
            }
          });
        } else if (l.itemId && l.trackingType === 'Serial') {
          getAvailableSerials(activeCompany.id, l.itemId).then(serials => {
            setAvailableSerials(prev => ({ ...prev, [l.itemId]: serials }));
          });
        }
      });
    }
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Size-mode line edits: recompute Total SQft when L / H / Thickness / Pieces / sizeUnit change.
      // PurchaseReturn keys totals off `returnQty` (not `quantity`), so after the
      // helper writes `quantity = totalSqft` we mirror it into returnQty.
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
        updated[index].returnQty = updated[index].quantity;
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].taxRate = item.gstRate || 0;
          updated[index].taxable = (item.gstRate || 0) > 0;
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure };

          // Size mode: prime with item defaults and force unit to SQft.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
            updated[index].returnQty = updated[index].quantity;
          } else {
            updated[index].hasSize = false;
          }
        }
      }
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure };
        }
      }
      // Validate return qty doesn't exceed original
      if (field === 'returnQty' && updated[index].originalQty > 0) {
        updated[index].returnQty = Math.min(value, updated[index].originalQty);
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.description || (Number(line.returnQty || line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', parentId: '', parentDocNumber: '',
      reason: '', notes: '', status: 'Draft',
      whtRate: 0, whtAmount: 0,
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '',
      paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
      existingPaidAmount: 0
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingReturn(null);
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (ret) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load purchase-invoice link data on demand
    setEditingReturn(ret);
    setFormData({
      docDate: ret.docDate || new Date().toISOString().split('T')[0],
      partyId: ret.partyId || '', partyName: ret.partyName || '',
      parentId: ret.parentId || '',
      parentDocNumber: ret.parentDocNumber || '',
      reason: ret.reason || '', notes: ret.notes || '', status: ret.status || 'Draft',
      whtRate: ret.whtRate || 0, whtAmount: ret.whtAmount || 0,
      employeeId: ret.employeeId || '', employeeName: ret.employeeName || '',
      jobId: ret.jobId || '', jobName: ret.jobName || '',
      locationId: ret.locationId || '', locationName: ret.locationName || '',
      paymentAmount: ret.paymentAmount || 0, paymentAccountId: ret.paymentAccountId || '', paymentAccountName: ret.paymentAccountName || '',
      existingPaidAmount: Math.max(0, (ret.paidAmount || 0) - (ret.paymentAmount || 0))
    });
    setLineItems(ret.lines?.length > 0 ? ret.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };

  const handleView = (ret) => { setViewingReturn(ret); setViewMode('view'); };

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingReturn(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Save return using transactionHelper
  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a vendor'); return; }
    if (!formData.parentId) { alert('Please select a purchase invoice'); return; }
    if (!formData.reason.trim()) { alert('Please enter a reason for return'); return; }
    if (!activeFinancialYear?.id) { alert('Please select an active financial year'); return; }
    if (Number(formData.paymentAmount) > 0 && !formData.paymentAccountId) {
      alert('Please select a refund account'); return;
    }

    const validLines = lineItems.filter(l => l.description && l.returnQty > 0);
    if (validLines.length === 0) {
      alert('Please add at least one line with return quantity > 0');
      return;
    }

    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch selection is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const qty = line.returnQty || line.quantity || 0;
        if (serials.length !== qty) {
          alert(`"${line.itemName || line.description}" requires exactly ${qty} serial number(s). You selected ${serials.length}.`); return;
        }
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const calc = calculateLine(l);
        return {
          lineNumber: i + 1,
          itemId: l.itemId,
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description,
          originalQty: l.originalQty,
          returnQty: l.returnQty,
          quantity: l.returnQty, // For compatibility with subTransactionHelper
          unitPrice: l.unitPrice,
          unitCost: l.unitCost || l.unitPrice,
          unitOfMeasure: l.unitOfMeasure || '',
          taxable: l.taxable,
          taxRate: l.taxRate,
          taxAmount: calc.taxAmount,
          lineTotal: calc.lineTotal,
          batchNumber: l.batchNumber || '',
          serialNumbers: l.serialNumbers || []
        };
      });

      const status = saveStatus || formData.status || 'Draft';

      const transactionData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        partyType: 'vendor',
        partyId: formData.partyId,
        partyName: formData.partyName,
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        reason: formData.reason,
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        whtRate: Number(formData.whtRate) || 0,
        whtAmount: totals.whtAmount,
        totalAmount: totals.totalAmount,
        paymentAmount: Number(formData.paymentAmount) || 0,
        paymentAccountId: formData.paymentAccountId || null,
        paymentAccountName: formData.paymentAccountName || null,
        paidAmount: (Number(formData.existingPaidAmount) || 0) + (Number(formData.paymentAmount) || 0),
        balanceAmount: totals.totalAmount - (Number(formData.existingPaidAmount) || 0) - (Number(formData.paymentAmount) || 0),
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        notes: formData.notes,
        status,
        lines: calculatedLines
      };

      let savedTransaction;

      if (editingReturn) {
        // Update existing
        savedTransaction = await updateTransaction(
          activeCompany.id,
          editingReturn.id,
          transactionData,
          currentUser.uid
        );

        // Handle status change
        if (status !== editingReturn.status) {
          await updateTransactionStatus(activeCompany.id, editingReturn.id, status, currentUser.uid);

          // SubTransactions + StockFlow already regenerated by updateTransaction() above.
          // updateTransactionStatus also sets postedBy/postedAt.
          if (status === 'Posted' && editingReturn.status !== 'Posted') {
          }
        }

        savedTransaction = { ...savedTransaction, status };
      } else {
        // Create new
        savedTransaction = await createTransaction(
          activeCompany.id,
          transactionData,
          currentUser.uid
        );

        // createTransaction() already generated SubTransactions + StockFlow.
        // Just update status to Posted (sets postedBy/postedAt automatically).
        if (status === 'Posted') {
          await updateTransactionStatus(activeCompany.id, savedTransaction.id, status, currentUser.uid);

        }
      }

      // ACC-11 (2026-09-30): the parent invoice's returned amount / balance /
      // status is RE-COMPUTED from all its live returns (never incremented), so
      // saving, re-posting or editing a return can't double-count, and returns
      // are no longer booked as cash "paid" on the invoice.
      {
        const parentsToRecalc = new Set([formData.parentId, editingReturn?.parentId].filter(Boolean));
        for (const pid of parentsToRecalc) {
          try { await recalcInvoiceReturns(activeCompany.id, pid); } catch (err) { console.error('Error updating parent invoice:', err); }
        }
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editingReturn ? editingReturn.id : savedTransaction?.id;
      const savedRecord = editingReturn
        ? { ...editingReturn, ...transactionData, id: savedId, status }
        : { ...savedTransaction, status };
      applyLocal(prev => editingReturn
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved out — drop the per-item cached quantities so the next
      // "stock?" lookup on any form this session reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // D3: this save changed the parent invoice's paid/balance/status, so the
      // cached invoice link-data is stale — mark it for a refetch on the NEXT
      // form open (zero reads now) instead of re-scanning every PI here.
      formDataLoadedRef.current = false;

      // Background refresh — reconciles with server data without blocking UI.
      loadReturns().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving purchase return:', error);
      alert('Error saving purchase return: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ret) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${ret.docNumber}?`)) return;
    try {
      // ACC-04: deleteTransaction() checks first, then removes ledger + stock rows.
      await deleteTransaction(activeCompany.id, ret.id, currentUser.uid);
      // ACC-11: refresh the parent invoice from its remaining live returns.
      if (ret.parentId) {
        try { await recalcInvoiceReturns(activeCompany.id, ret.parentId); } catch (err) { console.error('Error reversing parent invoice balance:', err); }
      }
      // D3: parent invoice balances/statuses changed — mark the form link-data
      // stale so the NEXT form open refetches it, instead of paying for it here
      // on the list view.
      formDataLoadedRef.current = false;
      await loadReturns();
    } catch (error) { console.error('Error deleting return:', error); alert('Could not delete: ' + (error?.message || 'unknown error')); }
  };

  // Print
  const handlePrint = (ret) => {
    printDocument('purchaseReturn', ret || viewingReturn, activeCompany, printConfig, activeBranch);
  };

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by return#, vendor, invoice#..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Return</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading purchase returns...</LoadingMessage>
      ) : filteredReturns.length === 0 ? (
        <TranEmptyState>
          <FiRepeat size={48} />
          <h3>No Purchase Returns</h3>
          <p>{returns.length === 0 ? 'Create your first purchase return' : 'No returns match your filters'}</p>
          {returns.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Return</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">Return #</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Vendor</SortableTh>
                <SortableTh field="parentDocNumber">Invoice Ref</SortableTh>
                <SortableTh field="reason">Reason</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Amount</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredReturns.map(ret => (
                <Tr key={ret.id}>
                  <Td>
                    <strong>{ret.docNumber}</strong>
                    {ret.parentId && (
                      <LinkedBadge title={`Linked to ${ret.parentDocNumber}`}>
                        <FiLink size={10} />
                      </LinkedBadge>
                    )}
                  </Td>
                  <Td>{ret.docDate}</Td>
                  {showBranchCol && <Td>{ret.branchName || '-'}</Td>}
                  <Td>{ret.partyName}</Td>
                  <Td>{ret.parentDocNumber || '-'}</Td>
                  <Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ret.reason || '-'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {activeCompany?.currencyCode || 'PKR'} {formatAmount(ret.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[ret.status]}>{ret.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(ret)}><FiEye /></IconBtn>
                      {canChange && <IconBtn title="Edit" onClick={() => handleEdit(ret)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(ret)}><FiPrinter /></IconBtn>
                      {ret.status !== 'Draft' && ret.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="purchaseReturn" doc={ret} party={vendors.find(v => v.id === ret.partyId)} variant="icon" />
                      )}
                      {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(ret)}><FiTrash2 /></IconBtn>}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredReturns.length : pagination.totalCount}</strong> return(s)</span>
        <span>
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Posted: <strong>{listSummary.counts.Posted || 0}</strong> |{' '}
          Cancelled: <strong>{listSummary.counts.Cancelled || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingReturn) return null;
    const ret = viewingReturn;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingReturn(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {ret.docNumber}
            <StatusBadge $color={STATUS_COLORS[ret.status]} style={{ marginLeft: 12 }}>{ret.status}</StatusBadge>
            {ret.parentId && (
              <LinkedBadge style={{ marginLeft: 8 }} title={`Linked to ${ret.parentDocNumber}`}>
                <FiLink size={12} /> {ret.parentDocNumber}
              </LinkedBadge>
            )}
          </FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(ret)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(ret)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Return Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Return Number</label><span>{ret.docNumber}</span></InfoItem>
            <InfoItem><label>Return Date</label><span>{ret.docDate}</span></InfoItem>
            <InfoItem><label>Vendor</label><span>{ret.partyName}</span></InfoItem>
            <InfoItem><label>Original Invoice</label><span>{ret.parentDocNumber || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{ret.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{ret.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{ret.locationName || '-'}</span></InfoItem>
            <InfoItem style={{ gridColumn: 'span 2' }}><label>Reason for Return</label><span>{ret.reason}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        <FormCard>
          <SectionTitle>Return Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Orig. Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Return Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Tax</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(ret.lines || []).map((line, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.originalQty || '-'}</Td>
                    <Td style={{ textAlign: 'right', color: colors.error.main, fontWeight: 500 }}>{line.returnQty || line.quantity} {line.unitOfMeasure && <span style={{ fontSize: '11px', color: '#64748B' }}>{line.unitOfMeasure}</span>}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.taxable ? formatAmount(line.taxAmount) : '-'}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 500 }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(ret.subtotal)}</td></tr>
                <tr><td>Tax</td><td>{formatAmount(ret.taxAmount)}</td></tr>
                {ret.whtAmount > 0 && <tr><td>WHT ({ret.whtRate || 0}%)</td><td>-{formatAmount(ret.whtAmount)}</td></tr>}
                <TotalRow><td>Total Return</td><td>{activeCompany?.currencyCode || 'PKR'} {formatAmount(ret.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        {ret.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{ret.notes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={ret?.id} attachment={ret?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => {
    // Only this branch's invoices (see utils/branchLink.js).
    const vendorInvoices = postedInvoices.filter(inv => !formData.partyId || inv.partyId === formData.partyId)
      .filter(inv => inActiveBranch(inv, activeBranch, formData.parentId));

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>{editingReturn ? `Edit: ${editingReturn.docNumber}` : 'New Purchase Return'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
              <FiX /> Cancel
            </SaveButton>
            {canEdit && !(editingReturn && editingReturn.status && editingReturn.status !== 'Draft' && editingReturn.status !== 'Cancelled') && (
              <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
              </SaveButton>
            )}
            {canEdit && (formData.status === 'Draft' || !editingReturn) && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}>
                <FiCheck /> Save & Post
              </SaveButton>
            )}
            {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
            {canEdit && (editingReturn && editingReturn.status && editingReturn.status !== 'Draft' && editingReturn.status !== 'Cancelled') && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Return Details</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Return Number</Label>
              <Input value={editingReturn?.docNumber || 'Auto-generated'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Return Date *</Label>
              <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input value={formData.status} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Vendor *</Label>
              <VendorSelect vendors={vendors} selectedVendorId={formData.partyId} onChange={handleVendorChange} disabled={!!formData.parentId} balanceOnDemand />
            </FormGroup>
            <FormGroup $span2>
              <Label>Purchase Invoice *</Label>
              <Select value={formData.parentId} onChange={e => handleInvoiceChange(e.target.value)}>
                <option value="">-- Select Invoice --</option>
                {vendorInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.docNumber} - {inv.partyName} ({inv.currency || activeCompany?.currencyCode || 'PKR'} {formatAmount(inv.totalAmount)})
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup $span2>
              <Label>Reason for Return *</Label>
              <Input value={formData.reason} onChange={e => handleHeaderChange('reason', e.target.value)}
                placeholder="e.g., Damaged goods, wrong items, quality issue..." />
            </FormGroup>
            <FormGroup>
              <Label>WHT Rate (%)</Label>
              <Input type="number" min="0" max="100" step="0.5" value={formData.whtRate}
                onChange={e => handleHeaderChange('whtRate', Number(e.target.value))}
                placeholder="0" />
            </FormGroup>
            <FormGroup>
              <Label>Employee</Label>
              <Select value={formData.employeeId} onChange={e => {
                const emp = employees.find(em => em.id === e.target.value);
                handleHeaderChange('employeeId', e.target.value);
                handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
              }}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Job</Label>
              <Select value={formData.jobId} onChange={e => {
                const job = jobs.find(j => j.id === e.target.value);
                handleHeaderChange('jobId', e.target.value);
                handleHeaderChange('jobName', job?.jobName || '');
              }}>
                <option value="">-- Select Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>{reqLabel(activeCompany, 'Location')}</Label>
              <Select value={formData.locationId} onChange={e => {
                const loc = locations.find(l => l.id === e.target.value);
                handleHeaderChange('locationId', e.target.value);
                handleHeaderChange('locationName', loc?.locationName || '');
              }}>
                <option value="">-- Select Location --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.locationName}</option>
                ))}
              </Select>
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Return Items</SectionTitle>
            {!formData.parentId && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
          </LineItemsHeader>

          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                  <LineTh style={{ width: 300, minWidth: 300, maxWidth: 300 }}>Description</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Orig. Qty</LineTh>
                  <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Return Qty</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                  <LineTh style={{ width: 80, minWidth: 80, maxWidth: 80 }}>Tax?</LineTh>
                  <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Tax %</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Batch</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => {
                  const calc = calculateLine(line);
                  return (
                    <React.Fragment key={index}><tr>
                      <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                      <LineTd>
                        <LineInput
                          ref={(el) => { lineItemRefs.current[index] = el; }}
                          value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Item description" disabled={!!formData.parentId} />
                        
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" value={line.originalQty} disabled
                          style={{ textAlign: 'right', background: colors.neutral[50] }} />
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LineInput type="number" min="0" max={line.originalQty || 999999} value={line.returnQty}
                            onChange={e => handleLineChange(index, 'returnQty', Number(e.target.value))}
                            disabled={line.hasSize}
                            title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                            style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined }} />
                          <LineUnitPicker
                            item={items.find(i => i.id === line.itemId)}
                            line={line}
                            onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                          />
                        </div>
                        <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                          quantity={line.returnQty} hasSize={line.hasSize}
                          onChange={(q) => handleLineChange(index, 'returnQty', q)} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" step="0.01" value={line.unitPrice}
                          onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))}
                          style={{ textAlign: 'right' }} disabled={!!formData.parentId} />
                      </LineTd>
                      <LineTd style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={line.taxable}
                          onChange={e => handleLineChange(index, 'taxable', e.target.checked)}
                          disabled={!!formData.parentId} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" max="100" value={line.taxRate}
                          onChange={e => handleLineChange(index, 'taxRate', Number(e.target.value))}
                          onKeyDown={(e) => handleLastFieldEnter(e, index)}
                          disabled={!line.taxable || !!formData.parentId}
                          style={{ textAlign: 'right', opacity: line.taxable ? 1 : 0.4 }} />
                      </LineTd>
                      <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>{line.trackingType === 'Batch' ? (
                          <select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120, border: '1px solid #ddd', borderRadius: '4px' }}>
                            <option value="">Select Batch</option>
                            {(availableBatches[line.itemId] || []).map(b => (
                              <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining})</option>
                            ))}
                          </select>
                        ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Serial') && (
                        <LineTd>{line.trackingType === 'Serial' ? (
                          <SerialSelectPopover
                            availableSerials={availableSerials[line.itemId] || []}
                            selectedSerials={line.serialNumbers || []}
                            requiredCount={line.returnQty || line.quantity || 0}
                            onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)}
                          />
                        ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                      )}
                      <LineTd style={{ textAlign: 'center' }}>
                        {!formData.parentId && lineItems.length > 1 && (
                          <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>
                        )}
                      </LineTd>
                    </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={!!formData.parentId} leftColSpan={1} />)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LineTable>
          </LineTableContainer>

          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
                <tr><td>Tax</td><td>{formatAmount(totals.taxAmount)}</td></tr>
                {totals.whtAmount > 0 && <tr><td>WHT ({formData.whtRate}%)</td><td>-{formatAmount(totals.whtAmount)}</td></tr>}
                <TotalRow><td>Total Return</td><td>{activeCompany?.currencyCode || 'PKR'} {formatAmount(totals.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>

          <PaymentSection>
            <SectionTitle>Refund</SectionTitle>
            <FormGrid>
              {formData.existingPaidAmount > 0 && (
                <FormGroup>
                  <Label>Received via Vouchers</Label>
                  <Input value={formatAmount(formData.existingPaidAmount)} disabled
                    style={{ fontWeight: 600, color: colors.success.main }} />
                </FormGroup>
              )}
              <FormGroup>
                <Label>Refund Amount</Label>
                <Input type="number" min="0" step="0.01" value={formData.paymentAmount}
                  onChange={e => handleHeaderChange('paymentAmount', Math.min(Number(e.target.value), totals.totalAmount - formData.existingPaidAmount))}
                  placeholder="0.00" />
              </FormGroup>
              <FormGroup>
                <Label>Refund Account</Label>
                <Select value={formData.paymentAccountId} onChange={e => {
                  const acc = paymentAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('paymentAccountId', e.target.value);
                  handleHeaderChange('paymentAccountName', acc?.name || '');
                }}>
                  <option value="">-- Select Account --</option>
                  <optgroup label="Cash Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11101')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bank Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11102')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                </Select>
              </FormGroup>
              {(formData.existingPaidAmount > 0 || Number(formData.paymentAmount) > 0) && (
                <FormGroup>
                  <Label>Balance After Refund</Label>
                  <Input value={formatAmount(totals.totalAmount - formData.existingPaidAmount - (Number(formData.paymentAmount) || 0))} disabled
                    style={{ fontWeight: 600, color: colors.error.main }} />
                </FormGroup>
              )}
            </FormGrid>
          </PaymentSection>
        </FormCard>

        <FormCard>
          <SectionTitle>Additional Information</SectionTitle>
          <FormGrid>
            <FormGroup $span2>
              <Label>Notes</Label>
              <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <AttachmentPanel
          transactionId={editingReturn?.id}
          attachment={editingReturn?.attachment}
          canEdit={canEdit}
          onChange={({ attachment }) => setEditingReturn(prev => prev ? { ...prev, attachment } : prev)}
        />
      </>
    );
  };

  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Posted') : undefined,
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Purchase Return (Debit Note)" breadcrumb="Purchases / Purchase Return">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ LOCAL STYLED COMPONENTS ============
const LinkedBadge = styled.span`display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:500;color:${colors.info.main};background:${colors.info.main}15;margin-left:6px;`;

export default PurchaseReturn;
